/**
 * KAN-746: фильтры списка заказов в URL: ?tab=carts|placed|approved|cancelled,
 * ?search=, ?manager=<id>|none|all, ?client=<id>. Вкладка по умолчанию: корзины.
 * KAN-909: менеджер по умолчанию - свой (useManagerFilterChoice), выбор пишется в адрес
 * (Все = manager=all) и запоминается; удалённый менеджер сбрасывается к умолчанию.
 */
import { useCallback } from 'react';
import { ALL_MANAGERS, NO_MANAGER } from '@/components/clients/useClientListFilters';
import { useDropUnknownManager, useManagerFilterChoice } from '@/hooks/useManagerFilterChoice';
import { useManagers } from '@/hooks/useManagers';
import { useUrlFilters, useUrlSearch } from '@/hooks/useUrlFilters';
import type { ManagerChoiceValues } from '@/lib/managerFilterChoice';
import { ORDER_TABS } from '@/types/order';
import type { OrderTab } from '@/types/order';

export const DEFAULT_ORDER_TAB: OrderTab = 'carts';

const ORDER_MANAGER_VALUES: ManagerChoiceValues = { all: ALL_MANAGERS, mine: (id) => id };
const ORDER_SPECIAL_MANAGERS = [ALL_MANAGERS, NO_MANAGER];

const toTab = (value: string | null): OrderTab =>
  (ORDER_TABS as readonly string[]).includes(value ?? '') ? (value as OrderTab) : DEFAULT_ORDER_TAB;

export interface OrderFilters {
  tab: OrderTab;
  search: string;
  searchInput: string;
  /** Значение фильтра менеджера: 'all', 'none' или id. */
  manager: string;
  client: string | null;
  setTab: (tab: OrderTab) => void;
  setSearchInput: (value: string) => void;
  setManager: (value: string) => void;
  clearClient: () => void;
}

export function useOrderFilters(): OrderFilters {
  const { searchParams, update } = useUrlFilters();
  const tab = toTab(searchParams.get('tab'));
  const { value: manager, choose: chooseManager, clear: clearManager } = useManagerFilterChoice(
    'orders',
    ORDER_MANAGER_VALUES,
    searchParams.get('manager')
  );
  const client = searchParams.get('client') || null;
  const { search, searchInput, setSearchInput } = useUrlSearch(searchParams, update);

  const setTab = useCallback(
    (value: OrderTab) => update('tab', value === DEFAULT_ORDER_TAB ? null : value),
    [update]
  );
  const setManager = useCallback(
    (value: string) => {
      chooseManager(value);
      update('manager', value);
    },
    [chooseManager, update]
  );
  const dropManager = useCallback(() => {
    clearManager();
    update('manager', null);
  }, [clearManager, update]);
  const { data: managers } = useManagers('all');
  useDropUnknownManager(manager, managers, ORDER_SPECIAL_MANAGERS, dropManager);
  const clearClient = useCallback(() => update('client', null), [update]);

  return { tab, search, searchInput, manager, client, setTab, setSearchInput, setManager, clearClient };
}
